import React, { useEffect, useCallback, useRef } from "react";
import "./BottomSheet.scss";

const BottomSheet = ({
    isOpen,
    onClose,
    children,
    title,
    height = "auto",
    className = "",
    showHandle = true,
    closeOnBackdropClick = true,
    closeOnEscape = true,
}) => {
    // ESC 키로 닫기 기능
    const handleKeyDown = useCallback(
        (event) => {
            if (closeOnEscape && event.key === "Escape" && isOpen) {
                onClose();
            }
        },
        [closeOnEscape, isOpen, onClose]
    );

    // 배경 클릭 시 닫기 기능
    const handleBackdropClick = useCallback(
        (event) => {
            if (closeOnBackdropClick && event.target === event.currentTarget) {
                onClose();
            }
        },
        [closeOnBackdropClick, onClose]
    );

    // 소프트 키보드 대응.
    // iOS(WKWebView)는 키보드가 떠도 layout viewport/`position:fixed`가 줄지 않아
    // 하단 sticky 액션 바가 키보드에 가려진다. VisualViewport에 맞춰 오버레이를
    // 실제 가시 영역으로 축소하고(시트는 flex-end로 그 하단에 붙음), 키보드가 올라온
    // 동안에는 시트 높이를 가시 영역 내로 제한(--kb)해 액션 바/입력창이 항상 보이게 한다.
    const overlayRef = useRef(null);
    useEffect(() => {
        const vv = window.visualViewport;
        const overlay = overlayRef.current;
        if (!isOpen || !vv || !overlay) return undefined;

        const sync = () => {
            overlay.style.top = `${vv.offsetTop}px`;
            overlay.style.height = `${vv.height}px`;
            overlay.style.bottom = "auto";
            const keyboardOpen = window.innerHeight - vv.height > 80;
            overlay.classList.toggle("bottom-sheet-overlay--kb", keyboardOpen);
        };

        sync();
        vv.addEventListener("resize", sync);
        vv.addEventListener("scroll", sync);

        return () => {
            vv.removeEventListener("resize", sync);
            vv.removeEventListener("scroll", sync);
            overlay.style.top = "";
            overlay.style.height = "";
            overlay.style.bottom = "";
            overlay.classList.remove("bottom-sheet-overlay--kb");
        };
    }, [isOpen]);

    // 입력창 focus 시 해당 필드를 시트 가시 영역 안으로 스크롤.
    // iOS/Android WebView 는 키보드 애니메이션으로 visualViewport 높이가 늦게 줄어들므로
    // focus 즉시가 아니라 지연 재시도(150/300/500ms)로 확인한다. 데스크톱 회귀(불필요한
    // 스크롤 점프)를 막기 위해 실제 키보드가 열렸을 때만 동작.
    useEffect(() => {
        const overlay = overlayRef.current;
        if (!isOpen || !overlay) return undefined;

        const timers = [];
        const isField = (el) =>
            !!el &&
            typeof el.matches === "function" &&
            el.matches("input, textarea, select, [contenteditable='true']");

        const handleFocusIn = (event) => {
            const el = event.target;
            if (!isField(el)) return;
            const scrollIfKeyboardOpen = () => {
                const vv = window.visualViewport;
                const keyboardOpen = vv ? window.innerHeight - vv.height > 80 : false;
                if (keyboardOpen && document.activeElement === el) {
                    el.scrollIntoView({ block: "center", behavior: "smooth" });
                }
            };
            [150, 300, 500].forEach((delay) => {
                timers.push(window.setTimeout(scrollIfKeyboardOpen, delay));
            });
        };

        overlay.addEventListener("focusin", handleFocusIn);
        return () => {
            overlay.removeEventListener("focusin", handleFocusIn);
            timers.forEach((id) => window.clearTimeout(id));
        };
    }, [isOpen]);

    // 키보드 이벤트 리스너 등록/해제
    useEffect(() => {
        if (isOpen) {
            document.addEventListener("keydown", handleKeyDown);
            // 바디 스크롤 방지
            document.body.style.overflow = "hidden";
        } else {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "unset";
        }

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, handleKeyDown]);

    return (
        <div
            ref={overlayRef}
            className={`bottom-sheet-overlay ${
                isOpen ? "bottom-sheet-overlay--open" : ""
            }`}
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? "bottom-sheet-title" : undefined}
            style={{
                pointerEvents: isOpen ? "auto" : "none",
            }}
        >
            <div
                className={`bottom-sheet ${
                    isOpen ? "bottom-sheet--open" : ""
                } ${className}`}
                style={{ height }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* 내부 닫기 버튼 */}
                {showHandle && (
                    <button
                        type="button"
                        className="bottom-sheet__inner-close-button"
                        onClick={onClose}
                        aria-label="바텀시트 닫기"
                        tabIndex={isOpen ? 0 : -1}
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M18 6L6 18M6 6L18 18"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                )}

                {title && (
                    <div className="bottom-sheet__header">
                        <h2
                            id="bottom-sheet-title"
                            className="bottom-sheet__title"
                        >
                            {title}
                        </h2>
                    </div>
                )}

                <div className="bottom-sheet__content">{children}</div>
            </div>
        </div>
    );
};

export default BottomSheet;
